import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, X, Send, Users, BookOpen, CalendarDays, MessageSquare, Bell, Layers, Ban, ShieldCheck, ImageOff, Eye, Download, FileText, UserPlus, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Avatar from "@/components/Avatar";
import AdminEvents from "@/components/admin/AdminEvents";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

/* ---------- Modal ---------- */
function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="admin-modal">
      <div className="bg-mahogany-950 gold-frame rounded-sm w-full max-w-lg max-h-[88vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl text-cream">{title}</h3>
          <button onClick={onClose} className="text-cream/60 hover:text-fuchsia" data-testid="modal-close"><X size={22} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- Field ---------- */
function Field({ f, value, onChange, options }) {
  const common = { value: value ?? "", onChange: (e) => onChange(f.key, e.target.value), className: "input-line", "data-testid": `field-${f.key}` };
  return (
    <div>
      <label className="text-cream/50 text-xs uppercase tracking-wider block mb-1">{f.label}</label>
      {f.type === "textarea" ? <textarea rows={3} {...common} className="input-line resize-none" />
        : f.type === "select" ? (
          <select {...common} className="input-line">
            <option value="" className="bg-mahogany-900">Select…</option>
            {(options || f.options || []).map((o) => <option key={o.value} value={o.value} className="bg-mahogany-900">{o.label}</option>)}
          </select>
        ) : f.type === "checkbox" ? (
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(f.key, e.target.checked)} className="accent-fuchsia w-5 h-5" data-testid={`field-${f.key}`} />
        ) : <input type={f.type === "date" ? "date" : "text"} {...common} />}
    </div>
  );
}

/* ---------- Generic CRUD ---------- */
function Crud({ label, path, fields, columns, optionsMap = {}, defaults = {}, rowActions }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get(path).then(({ data }) => setItems(data || [])), [path]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = { ...editing };
    delete payload._id;
    try {
      if (editing.id) await api.put(`${path}/${editing.id}`, payload);
      else await api.post(path, payload);
      toast.success(`${label} saved.`);
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; await api.delete(`${path}/${id}`); toast("Deleted."); load(); };

  return (
    <div data-testid={`crud-${label.toLowerCase()}`}>
      <div className="flex justify-between items-center mb-6">
        <p className="text-cream/60 text-sm">{items.length} {label.toLowerCase()}</p>
        <button onClick={() => setEditing({ ...defaults })} className="btn-primary !py-2.5 !px-5 inline-flex items-center gap-2" data-testid={`add-${label.toLowerCase()}`}><Plus size={16} /> Add {label}</button>
      </div>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between bg-mahogany-800/40 gold-frame rounded-sm p-4" data-testid={`row-${it.id}`}>
            <div className="min-w-0 flex-1">{columns(it)}</div>
            <div className="flex items-center gap-2 shrink-0 ml-4">
              {rowActions && rowActions(it, load)}
              <button onClick={() => setEditing(it)} className="w-9 h-9 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-mahogany-900" data-testid={`edit-${it.id}`}><Pencil size={15} /></button>
              <button onClick={() => del(it.id)} className="w-9 h-9 rounded-full border border-red-400/30 flex items-center justify-center text-red-400 hover:bg-red-400 hover:text-mahogany-900" data-testid={`del-${it.id}`}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? `Edit ${label}` : `Add ${label}`}>
        <div className="space-y-5">
          {fields.map((f) => <Field key={f.key} f={f} value={editing?.[f.key]} options={optionsMap[f.key]} onChange={(k, v) => setEditing({ ...editing, [k]: v })} />)}
          <button onClick={save} className="btn-primary w-full" data-testid="modal-save">Save {label}</button>
        </div>
      </Modal>
    </div>
  );
}

/* ---------- Overview ---------- */
function AdminOverview() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/members/metrics").then(({ data }) => setM(data)); }, []);
  if (!m) return <p className="text-cream/50">Loading metrics…</p>;
  const cards = [
    ["Total Members", m.total, Users], ["New This Month", m.new_this_month, UserPlus],
    ["New This Week", m.new_this_week, CalendarClock], ["Active Members", m.active, ShieldCheck],
  ];
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {cards.map(([l, n, Icon]) => (
          <div key={l} className="bg-mahogany-800/40 gold-frame rounded-sm p-6" data-testid={`metric-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <Icon size={20} className="text-gold mb-3" strokeWidth={1.25} />
            <p className="font-serif text-4xl text-cream">{n}</p>
            <p className="text-cream/50 uppercase tracking-wider text-[10px] mt-1">{l}</p>
          </div>
        ))}
      </div>
      <p className="overline mb-4">Recently Joined</p>
      <div className="space-y-2" data-testid="recent-members">
        {m.recent.map((r) => (
          <div key={r.id} className="flex items-center justify-between bg-mahogany-800/40 gold-frame rounded-sm p-4">
            <div><p className="text-cream">{r.name}</p><p className="text-cream/50 text-sm">{r.email} · {[r.city, r.state].filter(Boolean).join(", ")}</p></div>
            <span className="text-cream/40 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Members ---------- */
function Members() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [sort, setSort] = useState("newest");
  const [record, setRecord] = useState(null);
  const [notes, setNotes] = useState("");
  const load = () => api.get("/members").then(({ data }) => setMembers(data || []));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Remove this member?")) return; await api.delete(`/members/${id}`); toast("Removed."); load(); };
  const toggleStatus = async (mm) => {
    const status = mm.account_status === "suspended" ? "active" : "suspended";
    await api.put(`/members/${mm.id}/status`, { status });
    toast.success(status === "suspended" ? "Member suspended." : "Member reactivated.");
    load();
  };
  const rmPhoto = async (id) => { await api.delete(`/members/${id}/photo`); toast("Profile photo removed."); load(); };
  const openRecord = (mm) => { setRecord(mm); setNotes(mm.admin_notes || ""); };
  const saveNotes = async () => { await api.put(`/members/${record.id}/notes`, { notes }); toast.success("Notes saved."); setMembers((prev) => prev.map((x) => x.id === record.id ? { ...x, admin_notes: notes } : x)); };

  const exportCsv = () => {
    const cols = ["display_name", "first_name", "last_name", "email", "city", "state", "account_status", "referral", "email_consent", "genres", "created_at"];
    const esc = (v) => `"${String(Array.isArray(v) ? v.join("; ") : (v ?? "")).replace(/"/g, '""')}"`;
    const rows = [cols.join(",")].concat(members.filter((x) => x.role !== "admin").map((x) => cols.map((c) => esc(x[c])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "mahogany-room-members.csv"; a.click();
  };

  let shown = members.filter((mm) => {
    const s = q.toLowerCase();
    const match = !s || (mm.name || "").toLowerCase().includes(s) || (mm.email || "").toLowerCase().includes(s) || (mm.city || "").toLowerCase().includes(s);
    const st = statusF === "all" || (mm.account_status || "active") === statusF;
    return match && st;
  });
  shown = shown.slice().sort((a, b) => sort === "newest" ? (b.created_at || "").localeCompare(a.created_at || "") : (a.created_at || "").localeCompare(b.created_at || ""));

  const Row = ({ label, value }) => value ? (<div><p className="text-cream/50 text-xs uppercase tracking-wider">{label}</p><p className="text-cream">{Array.isArray(value) ? value.join(", ") : value}</p></div>) : null;

  return (
    <div data-testid="crud-members">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <p className="text-cream/60 text-sm flex-1">{members.filter((x) => x.role !== "admin").length} members</p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, city" className="input-line max-w-[200px]" data-testid="member-search" />
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="input-line max-w-[140px]" data-testid="member-status-filter">
          <option value="all" className="bg-mahogany-900">All statuses</option>
          <option value="active" className="bg-mahogany-900">Active</option>
          <option value="suspended" className="bg-mahogany-900">Inactive</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="input-line max-w-[130px]" data-testid="member-sort">
          <option value="newest" className="bg-mahogany-900">Newest</option>
          <option value="oldest" className="bg-mahogany-900">Oldest</option>
        </select>
        <button onClick={exportCsv} className="btn-secondary !py-2 !px-4 !text-xs inline-flex items-center gap-2" data-testid="export-csv"><Download size={14} /> CSV</button>
      </div>
      <div className="space-y-3">
        {shown.map((mm) => (
          <div key={mm.id} className="flex items-center justify-between bg-mahogany-800/40 gold-frame rounded-sm p-4" data-testid={`member-${mm.id}`}>
            <button onClick={() => openRecord(mm)} className="flex items-center gap-3 min-w-0 text-left flex-1" data-testid={`member-record-${mm.id}`}>
              <Avatar src={mm.picture} name={mm.display_name || mm.name} size={44} />
              <div className="min-w-0">
                <p className="text-cream truncate">
                  {mm.display_name || mm.name}
                  {mm.role === "admin" && <span className="text-gold text-xs ml-2 uppercase">Admin</span>}
                  {mm.account_status === "suspended" && <span className="text-red-300 text-xs ml-2 uppercase">Inactive</span>}
                  {mm.is_demo && <span className="text-caramel/70 text-xs ml-2 uppercase">Demo</span>}
                </p>
                <p className="text-cream/50 text-sm truncate">{mm.email} · {[mm.city, mm.state].filter(Boolean).join(", ")} · joined {mm.created_at ? new Date(mm.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</p>
              </div>
            </button>
            {mm.role !== "admin" && (
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <Link to={`/members/${mm.id}`} title="View public profile" className="w-9 h-9 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-mahogany-900" data-testid={`member-view-${mm.id}`}><Eye size={15} /></Link>
                {mm.picture && <button onClick={() => rmPhoto(mm.id)} title="Remove photo" className="w-9 h-9 rounded-full border border-gold/30 flex items-center justify-center text-cream/70 hover:text-fuchsia" data-testid={`member-rmphoto-${mm.id}`}><ImageOff size={15} /></button>}
                <button onClick={() => toggleStatus(mm)} title={mm.account_status === "suspended" ? "Reactivate" : "Deactivate"} className="w-9 h-9 rounded-full border border-gold/30 flex items-center justify-center text-cream/70 hover:text-gold" data-testid={`member-status-${mm.id}`}>{mm.account_status === "suspended" ? <ShieldCheck size={15} /> : <Ban size={15} />}</button>
                <button onClick={() => del(mm.id)} title="Remove" className="w-9 h-9 rounded-full border border-red-400/30 flex items-center justify-center text-red-400 hover:bg-red-400 hover:text-mahogany-900" data-testid={`member-del-${mm.id}`}><Trash2 size={15} /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Modal open={!!record} onClose={() => setRecord(null)} title="Member Record">
        {record && (
          <div className="space-y-6" data-testid="member-record-modal">
            <div className="flex items-center gap-4">
              <Avatar src={record.picture} name={record.display_name || record.name} size={64} />
              <div><p className="font-serif text-2xl text-cream">{record.display_name || record.name}</p><p className="text-cream/50 text-sm capitalize">{record.account_status === "suspended" ? "Inactive" : "Active"} member</p></div>
            </div>
            <div>
              <p className="overline mb-3">Member Information</p>
              <div className="grid grid-cols-2 gap-4">
                <Row label="Full Name" value={record.name} />
                <Row label="Email" value={record.email} />
                <Row label="Location" value={[record.city, record.state].filter(Boolean).join(", ")} />
                <Row label="Join Date" value={record.created_at ? new Date(record.created_at).toLocaleString() : ""} />
                <Row label="Sign-in Method" value={record.auth_provider} />
              </div>
            </div>
            <div>
              <p className="overline mb-3">Submitted at Signup</p>
              <div className="grid grid-cols-2 gap-4">
                <Row label="Favorite Genres" value={record.genres} />
                <Row label="How They Heard" value={record.referral} />
                <Row label="Email Consent" value={record.email_consent ? "Yes" : "No"} />
                <Row label="In Directory" value={record.directory_visible ? "Yes" : "No"} />
              </div>
              {record.hopes && <div className="mt-4"><p className="text-cream/50 text-xs uppercase tracking-wider">Hoping to Find</p><p className="text-cream">{record.hopes}</p></div>}
            </div>
            <div>
              <p className="overline mb-2">Admin Notes <span className="text-cream/40 normal-case tracking-normal">(private — never shown to member)</span></p>
              <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="input-line resize-none" placeholder="Internal notes about this member…" data-testid="admin-notes-input" />
              <button onClick={saveNotes} className="btn-primary !py-2 !px-5 !text-xs mt-3 inline-flex items-center gap-2" data-testid="save-notes"><FileText size={13} /> Save Notes</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ---------- Home Content ---------- */
function HomeContent() {
  const [c, setC] = useState({});
  useEffect(() => { api.get("/content/home").then(({ data }) => setC(data || {})); }, []);
  const save = async () => { await api.put("/content/home", { data: c }); toast.success("Homepage updated."); };
  const F = [
    { key: "hero_headline_1", label: "Hero Line 1" }, { key: "hero_headline_2", label: "Hero Line 2" },
    { key: "hero_headline_3", label: "Hero Line 3" }, { key: "hero_subtitle", label: "Hero Subtitle", type: "textarea" },
    { key: "mission_body", label: "Mission Paragraph", type: "textarea" },
  ];
  return (
    <div className="max-w-xl space-y-5" data-testid="crud-content">
      {F.map((f) => <Field key={f.key} f={f} value={c[f.key]} onChange={(k, v) => setC({ ...c, [k]: v })} />)}
      <button onClick={save} className="btn-primary" data-testid="content-save">Save Homepage</button>
    </div>
  );
}

export default function Admin() {
  const [seasons, setSeasons] = useState([]);
  const [books, setBooks] = useState([]);
  const [stats, setStats] = useState({ members: 0, books: 0, events: 0 });

  useEffect(() => {
    api.get("/seasons").then(({ data }) => setSeasons(data || []));
    api.get("/books").then(({ data }) => setBooks(data || []));
    Promise.all([api.get("/members/count"), api.get("/books"), api.get("/events")]).then(([m, b, e]) =>
      setStats({ members: m.data.count, books: b.data.length, events: e.data.length }));
  }, []);

  const seasonOpts = seasons.map((s) => ({ value: s.id, label: `${s.name} — ${s.subtitle}` }));
  const bookOpts = books.map((b) => ({ value: b.id, label: b.title }));

  const bookFields = [
    { key: "title", label: "Title" }, { key: "author", label: "Author" },
    { key: "description", label: "Description", type: "textarea" }, { key: "cover_url", label: "Cover Image URL" },
    { key: "category", label: "Category", type: "select", options: [{ value: "current", label: "Current Read" }, { value: "upcoming", label: "Upcoming" }, { value: "previous", label: "Previously Read" }, { value: "favorite", label: "Community Favorite" }] },
    { key: "season_id", label: "Season", type: "select" },
    { key: "theme", label: "Theme / Category" },
    { key: "month", label: "Month #" },
    { key: "reading_order", label: "Reading Order" },
    { key: "purchase_url", label: "Buy the Book URL" },
    { key: "audiobook_url", label: "Audiobook URL" },
    { key: "reading_deadline", label: "Reading Deadline", type: "date" },
    { key: "bn_url", label: "Barnes & Noble URL" }, { key: "bookshop_url", label: "Bookshop.org URL" },
  ];
  const seasonFields = [{ key: "name", label: "Season Name" }, { key: "subtitle", label: "Subtitle / Dates" }, { key: "order", label: "Order" }, { key: "active", label: "Active", type: "checkbox" }];
  const promptFields = [{ key: "question", label: "Question", type: "textarea" }, { key: "book_id", label: "Book", type: "select" }, { key: "month", label: "Month" }, { key: "active", label: "Active", type: "checkbox" }];
  const annFields = [{ key: "title", label: "Title" }, { key: "body", label: "Body", type: "textarea" }];

  const tabs = [
    ["overview", "Overview", Layers], ["books", "Books", BookOpen], ["seasons", "Seasons", Layers],
    ["events", "Events", CalendarDays], ["prompts", "Prompts", MessageSquare], ["announcements", "Announcements", Bell],
    ["members", "Members", Users], ["content", "Homepage", Pencil],
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-mahogany-950 to-mahogany-900 pt-32 pb-20 px-6 grain" data-testid="admin-page">
      <div className="max-w-5xl mx-auto">
        <p className="font-script text-3xl text-fuchsia mb-1">The Room, behind the scenes.</p>
        <h1 className="font-serif text-5xl text-cream mb-10">Admin Dashboard</h1>

        <Tabs defaultValue="overview">
          <TabsList className="bg-transparent flex flex-wrap gap-2 mb-10 h-auto justify-start" data-testid="admin-tabs">
            {tabs.map(([v, l, Icon]) => (
              <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-full border border-gold/30 px-5 py-2 text-cream/70 text-xs uppercase tracking-wider data-[state=active]:bg-gold data-[state=active]:text-mahogany-900 inline-flex items-center gap-2">
                <Icon size={13} /> {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview">
            <AdminOverview />
          </TabsContent>

          <TabsContent value="books">
            <Crud label="Book" path="/books" fields={bookFields} optionsMap={{ season_id: seasonOpts }} defaults={{ category: "upcoming" }}
              columns={(b) => (<div className="flex items-center gap-3">{b.cover_url && <img src={b.cover_url} className="w-9 rounded-sm" alt="" />}<div><p className="text-cream">{b.title} {b.category === "current" && <span className="text-fuchsia text-xs ml-1">● Current</span>}</p><p className="text-cream/50 text-sm">{b.author} · {b.category}</p></div></div>)} />
          </TabsContent>
          <TabsContent value="seasons">
            <Crud label="Season" path="/seasons" fields={seasonFields} defaults={{ order: 1, active: false }}
              columns={(s) => (<div><p className="text-cream">{s.name} {s.active && <span className="text-gold text-xs">active</span>}</p><p className="text-cream/50 text-sm">{s.subtitle}</p></div>)} />
          </TabsContent>
          <TabsContent value="events">
            <AdminEvents />
          </TabsContent>
          <TabsContent value="prompts">
            <Crud label="Prompt" path="/prompts" fields={promptFields} optionsMap={{ book_id: bookOpts }} defaults={{ active: true }}
              columns={(p) => (<div><p className="text-cream line-clamp-1">{p.question}</p><p className="text-cream/50 text-sm">{p.month} {p.active && <span className="text-gold">· active</span>}</p></div>)} />
          </TabsContent>
          <TabsContent value="announcements">
            <Crud label="Announcement" path="/announcements" fields={annFields}
              columns={(a) => (<div><p className="text-cream">{a.title}</p><p className="text-cream/50 text-sm line-clamp-1">{a.body}</p></div>)}
              rowActions={(a) => (
                <button onClick={async () => { const { data } = await api.post(`/announcements/${a.id}/broadcast`); toast.success(`Email sent to ${data.sent}/${data.recipients} members.`); }}
                  className="w-9 h-9 rounded-full border border-fuchsia/40 flex items-center justify-center text-fuchsia hover:bg-fuchsia hover:text-white" title="Email to members" data-testid={`broadcast-${a.id}`}><Send size={15} /></button>
              )} />
          </TabsContent>
          <TabsContent value="members"><Members /></TabsContent>
          <TabsContent value="content"><HomeContent /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
